import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface SelectedCountProps {
  /** Number of selected items */
  count: number
  /** Called when the count is clicked (optional - enables clickable styling) */
  onClick?: () => void
  /** Whether the selections view is active */
  active?: boolean
  /** Additional inline styles */
  style?: React.CSSProperties
  /** Additional CSS class name */
  className?: string
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled SelectedCount sub-component for MultiSelectHeader.
 *
 * Displays "N selected" text. When onClick is provided,
 * the text becomes clickable with hover styles.
 * All visual state determined by props.
 */
const SelectedCount = React.memo(
  ({
    count,
    onClick,
    active = false,
    style,
    className,
  }: SelectedCountProps) => {
    const [css, theme] = useStyletron()

    const isClickable = Boolean(onClick)

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isClickable) {
        onClick?.()
      }
    }

    const countClassName = css(getCountStyles(theme, { active, isClickable }))

    return (
      <span
        className={`${countClassName} ${className || ''}`}
        style={style}
        onClick={isClickable ? handleClick : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
      >
        {count} selected
      </span>
    )
  }
)

SelectedCount.displayName = 'SelectedCount'

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface CountStyleParams {
  active: boolean
  isClickable: boolean
}

const getCountStyles = (
  theme: any,
  { active, isClickable }: CountStyleParams
) => ({
  fontSize: '13px',
  color: active ? theme.colors.primary : theme.colors.grey50,
  marginLeft: '8px',
  ...(isClickable
    ? {
        cursor: 'pointer',
        textDecoration: active ? 'underline' : 'none',
        transition: 'color 150ms ease, text-decoration 150ms ease',
        ':hover': {
          color: theme.colors.primary,
          textDecoration: 'underline',
        },
      }
    : {}),
})

export default SelectedCount
